import { AvatarStackProps } from "../../components/visuals/Avatar";
import { getStatusBadgeOption } from "../../components/visuals/StatusBadge";
import {
  BadgeStatus,
  MilestoneKey,
  Priority,
} from "../../enums/Status";
import { TaskStatus } from "../../models/workflowModels";
import { MESSAGES } from "../../messages";
import {
  DataEntry,
  EngagementCategory,
  EngagementRecord,
  EngagementSubthemeMetric,
  EngagementType,
  Momentum,
  TopicMapping,
  TimelineActivityResponse,
  TimelineActivityNote,
} from "../../models/stewardshipModels";
import { Filter } from "../../types/PageFilterTypes";
import {
  formatReadable,
  formatReadableDate,
  isNotNan,
} from "../../utils/formattingUtils";
import { MILESTONE_TYPES } from "../CompanyProfile/constants";
import {
  ENGAGEMENT_META_THEME_IMAGES,
  ENGAGEMENT_MOMENTUM_BADGES,
  ENGAGEMENT_RECORD_ID_PARAM,
  MOMENTUM_DISPLAY_LIST,
} from "./constants";
import { EngagementBannerType } from "./components/EngagementBanner";
import {
  DEFAULT_PARENT_ESCALATIONS,
  EscalationParentType,
} from "./shared/EscalationAccordion";
import { MilestoneItem, MilestoneStep } from "./shared/MilestoneCard";

import {
  InteractionClass,
  LinkageType,
  SequenceFlow,
  INTERACTION_CLASS_DISPLAY,
  LINKAGE_TYPE_DISPLAY,
  SEQUENCE_FLOW_DISPLAY,
} from "../../models/engagementVotingMappingModels";
import { resolveEngagementMilestone } from "../../utils/engagementMilestones";

import consistentReinforcementImg from "../../assets/images/engagement_linkage/consistent_reinforcement.png";
import internalInconsistencyImg from "../../assets/images/engagement_linkage/internal_inconsistency.png";
import complementaryActionImg from "../../assets/images/engagement_linkage/complementary_action.png";
import activeUnderminingImg from "../../assets/images/engagement_linkage/active_undermining.png";
import isolatedActivityImg from "../../assets/images/engagement_linkage/isolated_activity.png";
import synergisticAlignmentImg from "../../assets/images/engagement_linkage/synergistic_alignment.png";
import escalationPathImg from "../../assets/images/engagement_linkage/escalation_path.png";
import thematicAlignmentImg from "../../assets/images/engagement_linkage/thematic_alignment.png";
import engagementPrecedesVoteImg from "../../assets/images/engagement_linkage/engagement_precedes_vote.png";
import votePrecedesEngagementImg from "../../assets/images/engagement_linkage/vote_precedes_engagement.png";
import bidirectionalImg from "../../assets/images/engagement_linkage/bidirectional.png";

export const buildEngagementOverview = (
  engagementRecord: EngagementRecord,
): DataEntry[] => {
  const activeMilestone: ActiveMilestone =
    determineActiveMilestone(engagementRecord);

  const extractedStatus = engagementRecord[activeMilestone.statusKey];
  const status = isNotNan(extractedStatus as string)
    ? extractedStatus
    : BadgeStatus.IN_PROGRESS;
  return [
    {
      label: "Investment Manager",
      value: formatReadable(engagementRecord.manager_id),
    },
    { label: "Engagement Type", value: engagementRecord.engagement_type },
    {
      label: "Start Date",
      value: formatReadableDate(
        engagementRecord.engagement_start || MESSAGES.NOT_AVAILABLE_TEXT,
      ),
    },
    {
      label: "Last Date",
      value: formatReadableDate(activeMilestone.lastDate),
    },
    {
      label: "Status",
      value: `${activeMilestone.milestone}: ${status}`,
    },
    {
      label: "Projected Completion Date",
      value:
        formatReadableDate(engagementRecord.proj_obj_completion) ||
        MESSAGES.NOT_AVAILABLE_TEXT,
    },
    { label: "Meta-Theme", value: formatReadable(engagementRecord.meta_theme) },
    { label: "Theme", value: formatReadable(engagementRecord.theme) },
    {
      label: "Sub-Theme",
      value: formatReadable(engagementRecord.sub_theme),
    },
  ];
};

export const buildEngagementBannerList = (
  engagementRecords: EngagementRecord[],
): EngagementBannerType[] => {
  return engagementRecords.map((record) => {
    return {
      recordId: record.engagement_record_id,
      theme: record.meta_theme as EngagementType,
      subTheme: record.sub_theme,
      manager: record.manager_name,
      managerId: record.manager_id,
      title: record.engagement_name,
      milestone: determineActiveMilestone(record).milestone,
      milestoneStart: record.engagement_start,
      endDate: record.proj_obj_completion,
      flagStatus: record.flag_status,
      category: record.engagement_type as EngagementCategory,
      escalationReached: getTopActiveEscalationParent(buildEscalations(record)),
      managerImage: isNotNan(record.image) ? record.image : null,
      record,
    };
  });
};

export const buildSearchParams = (id: string): string => {
  const idType = ENGAGEMENT_RECORD_ID_PARAM;
  const params = new URLSearchParams({
    [idType]: id,
  }).toString();
  return `?${params}`;
};

enum MilestoneColumnSuffix {
  Start = "_start",
  Status = "_status",
  Action = "_action",
}

type ActiveMilestone = {
  milestone: MilestoneKey;
  lastDate: string; // ISO date string
  statusKey: keyof EngagementRecord;
};

const determineActiveMilestone = (
  engagementRecord: EngagementRecord,
): ActiveMilestone => {
  const activeMilestone = resolveEngagementMilestone(engagementRecord);
  let lastDate = engagementRecord.engagement_start;
  const startKey = `${activeMilestone.toLowerCase()}${
    MilestoneColumnSuffix.Start
  }` as keyof EngagementRecord;
  const statusKey = `${activeMilestone.toLowerCase()}${
    MilestoneColumnSuffix.Status
  }` as keyof EngagementRecord;

  const startDate = engagementRecord[startKey] as string | undefined;
  if (isNotNan(startDate)) {
    lastDate = startDate || engagementRecord.engagement_start;
  }

  return { milestone: activeMilestone, lastDate, statusKey };
};

enum EngagementStatus {
  Complete = "Complete",
  Underway = "Underway",
  NotAvailable = "-",
}

const ENGAGEMENT_STATUS_BADGE_STATUS_MAPPING: Record<string, BadgeStatus> = {
  [EngagementStatus.Complete]: BadgeStatus.COMPLETE,
  [EngagementStatus.Underway]: BadgeStatus.IN_PROGRESS,
  [EngagementStatus.NotAvailable]: BadgeStatus.PLANNED,
};

export const buildMilestoneItem = (
  engagement: EngagementRecord,
  milestone: MilestoneKey,
): MilestoneItem => {
  const keys = {
    milestoneStart: `${milestone.toLowerCase()}${
      MilestoneColumnSuffix.Start
    }` as keyof EngagementRecord,
    milestoneStatus: `${milestone.toLowerCase()}${
      MilestoneColumnSuffix.Status
    }` as keyof EngagementRecord,
    milestoneAction: `${milestone.toLowerCase()}${
      MilestoneColumnSuffix.Action
    }` as keyof EngagementRecord,
  };

  const extractedDescription = engagement[keys.milestoneAction] as string;
  const description = isNotNan(extractedDescription)
    ? extractedDescription
    : "";

  return {
    milestoneId: parseInt(milestone.slice(1)),
    milestone: MilestoneStep[milestone as keyof typeof MilestoneStep],
    startDate: engagement[keys.milestoneStart] as string,
    description: description,
    status:
      ENGAGEMENT_STATUS_BADGE_STATUS_MAPPING[
        engagement[keys.milestoneStatus] as EngagementStatus
      ],
    lastCompletionDate: engagement[keys.milestoneAction] as string,
  };
};

export const buildEscalations = (
  engagement: EngagementRecord,
): EscalationParentType[] => {
  return DEFAULT_PARENT_ESCALATIONS.map((parent) => ({
    ...parent,
    escalations: parent.escalations.map((escalation) => {
      const keyPrefix = escalation.key.toLowerCase();
      const engagementRecordKey = Object.keys(engagement).find((key) =>
        key.includes(keyPrefix),
      );

      if (!engagementRecordKey) {
        return { ...escalation, isActive: false };
      }

      return {
        ...escalation,
        isActive: engagement[
          engagementRecordKey as keyof EngagementRecord
        ] as boolean,
      };
    }),
  }));
};

export const getTopActiveEscalationParent = (
  parents: EscalationParentType[],
): EscalationParentType | null => {
  let best: EscalationParentType | null = null;

  for (const p of parents) {
    if (p.escalations.some((e) => e.isActive)) {
      if (!best || p.key > best.key) best = p;
    }
  }
  return best;
};

export const buildBadgeStatusOptions = (
  firstKey?: Momentum,
  selectorName?: string,
): Filter[] => {
  const statusOptions: Filter[] = ENGAGEMENT_MOMENTUM_BADGES.map(
    (statusBadge) => ({
      key:
        MOMENTUM_DISPLAY_LIST.find(
          (item) => item.value === statusBadge.customText,
        )?.key || statusBadge.status,

      displayValue: statusBadge.customText || statusBadge.status,
      component: () =>
        getStatusBadgeOption(statusBadge.status, statusBadge.customText),
    }),
  );

  if (firstKey) {
    const index = statusOptions.findIndex((opt) => opt.key === firstKey);
    if (index > -1) {
      const [first] = statusOptions.splice(index, 1);
      statusOptions.unshift(first);
    }
  }

  if (selectorName) {
    statusOptions.forEach((option) => {
      option.selectorName = selectorName;
    });
  }

  return statusOptions;
};

export const findMilestoneIndex = (
  category: EngagementCategory,
  milestone: MilestoneKey,
): number => {
  const key =
    category === EngagementCategory.Issue ? MilestoneKey.CI : milestone;

  const idx = MILESTONE_TYPES.findIndex((m) => m.key === key);
  if (idx !== -1) return idx;

  const fallback = MILESTONE_TYPES.findIndex((m) => m.key === MilestoneKey.M1);
  return fallback !== -1 ? fallback : 0;
};

export const findSubThemeMetric = (
  engagementSubThemeMetrics: EngagementSubthemeMetric[],
  subTheme: string,
): EngagementSubthemeMetric | undefined =>
  engagementSubThemeMetrics.find((metric) => metric.sub_theme === subTheme);

export const getBadges = (mappings: TopicMapping[]): AvatarStackProps => {
  const MAX_COUNT = 99;
  enum LogoKey {
    logo = "logo",
    disabledLogo = "disabledLogo",
  }
  const avatars = mappings.map((mapping) => {
    const logoIndexingKey: LogoKey = mapping.is_mapped
      ? LogoKey.logo
      : LogoKey.disabledLogo;

    return {
      username: mapping.sub_theme,
      displayText: formatReadable(mapping?.sub_theme || ""),

      image:
        ENGAGEMENT_META_THEME_IMAGES[mapping.meta_theme]?.[logoIndexingKey],
      tooltip: true,
      ...mapping,
    };
  });

  return { items: avatars, maxCount: MAX_COUNT };
};

const ENGAGEMENT_LINKAGE_MESSAGES =
  MESSAGES.STEWARDSHIP.ENGAGEMENT_MODULE.ENGAGEMENT_LINKAGE;

// Interaction Class images and display config
// Behavioural relationship between managers involved in the records
export const INTERACTION_CLASS_CONFIG: Record<
  InteractionClass,
  { image: string; displayName: string; description: string }
> = {
  [InteractionClass.CONSISTENT_REINFORCEMENT]: {
    image: consistentReinforcementImg,
    displayName:
      INTERACTION_CLASS_DISPLAY[InteractionClass.CONSISTENT_REINFORCEMENT],
    description:
      ENGAGEMENT_LINKAGE_MESSAGES.INTERACTION_CLASS.CONSISTENT_REINFORCEMENT,
  },
  [InteractionClass.INTERNAL_INCONSISTENCY]: {
    image: internalInconsistencyImg,
    displayName:
      INTERACTION_CLASS_DISPLAY[InteractionClass.INTERNAL_INCONSISTENCY],
    description:
      ENGAGEMENT_LINKAGE_MESSAGES.INTERACTION_CLASS.INTERNAL_INCONSISTENCY,
  },
  [InteractionClass.SYNERGISTIC_ALIGNMENT]: {
    image: synergisticAlignmentImg,
    displayName:
      INTERACTION_CLASS_DISPLAY[InteractionClass.SYNERGISTIC_ALIGNMENT],
    description:
      ENGAGEMENT_LINKAGE_MESSAGES.INTERACTION_CLASS.SYNERGISTIC_ALIGNMENT,
  },
  [InteractionClass.ACTIVE_UNDERMINING]: {
    image: activeUnderminingImg,
    displayName: INTERACTION_CLASS_DISPLAY[InteractionClass.ACTIVE_UNDERMINING],
    description:
      ENGAGEMENT_LINKAGE_MESSAGES.INTERACTION_CLASS.ACTIVE_UNDERMINING,
  },
};

// Linkage Type images and display config
// Strategic relationship between the vote and the engagement
export const LINKAGE_TYPE_CONFIG: Record<
  LinkageType,
  { image: string; displayName: string; description: string }
> = {
  [LinkageType.THEMATIC_ALIGNMENT]: {
    image: thematicAlignmentImg,
    displayName: LINKAGE_TYPE_DISPLAY[LinkageType.THEMATIC_ALIGNMENT],
    description: ENGAGEMENT_LINKAGE_MESSAGES.LINKAGE_TYPE.THEMATIC_ALIGNMENT,
  },
  [LinkageType.COMPLEMENTARY_ACTION]: {
    image: complementaryActionImg,
    displayName: LINKAGE_TYPE_DISPLAY[LinkageType.COMPLEMENTARY_ACTION],
    description: ENGAGEMENT_LINKAGE_MESSAGES.LINKAGE_TYPE.COMPLEMENTARY_ACTION,
  },
  [LinkageType.ESCALATION_PATH]: {
    image: escalationPathImg,
    displayName: LINKAGE_TYPE_DISPLAY[LinkageType.ESCALATION_PATH],
    description: ENGAGEMENT_LINKAGE_MESSAGES.LINKAGE_TYPE.ESCALATION_PATH,
  },
  [LinkageType.ISOLATED_ACTIVITY]: {
    image: isolatedActivityImg,
    displayName: LINKAGE_TYPE_DISPLAY[LinkageType.ISOLATED_ACTIVITY],
    description: ENGAGEMENT_LINKAGE_MESSAGES.LINKAGE_TYPE.ISOLATED_ACTIVITY,
  },
};

// Sequence Flow images and display config
// Temporal sequence / causal flow between engagement and vote
export const SEQUENCE_FLOW_CONFIG: Record<
  SequenceFlow,
  { image: string; displayName: string; description: string }
> = {
  [SequenceFlow.ENGAGEMENT_PRECEDES_VOTE]: {
    image: engagementPrecedesVoteImg,
    displayName: SEQUENCE_FLOW_DISPLAY[SequenceFlow.ENGAGEMENT_PRECEDES_VOTE],
    description:
      ENGAGEMENT_LINKAGE_MESSAGES.SEQUENCE_FLOW.ENGAGEMENT_PRECEDES_VOTE,
  },
  [SequenceFlow.VOTE_PRECEDES_ENGAGEMENT]: {
    image: votePrecedesEngagementImg,
    displayName: SEQUENCE_FLOW_DISPLAY[SequenceFlow.VOTE_PRECEDES_ENGAGEMENT],
    description:
      ENGAGEMENT_LINKAGE_MESSAGES.SEQUENCE_FLOW.VOTE_PRECEDES_ENGAGEMENT,
  },
  [SequenceFlow.BIDIRECTIONAL]: {
    image: bidirectionalImg,
    displayName: SEQUENCE_FLOW_DISPLAY[SequenceFlow.BIDIRECTIONAL],
    description: ENGAGEMENT_LINKAGE_MESSAGES.SEQUENCE_FLOW.BIDIRECTIONAL,
  },
};

export const getInteractionClassImage = (
  interactionClass: InteractionClass,
): string => INTERACTION_CLASS_CONFIG[interactionClass]?.image || "";

export const getLinkageTypeImage = (linkageType: LinkageType): string =>
  LINKAGE_TYPE_CONFIG[linkageType]?.image || "";

export const getSequenceFlowImage = (sequenceFlow: SequenceFlow): string =>
  SEQUENCE_FLOW_CONFIG[sequenceFlow]?.image || "";

export const getInteractionClassTooltip = (
  interactionClass: InteractionClass,
): string => {
  const config = INTERACTION_CLASS_CONFIG[interactionClass];
  return config ? `${config.displayName}: ${config.description}` : "";
};

export const getLinkageTypeTooltip = (linkageType: LinkageType): string => {
  const config = LINKAGE_TYPE_CONFIG[linkageType];
  return config ? `${config.displayName}: ${config.description}` : "";
};

export const getSequenceFlowTooltip = (sequenceFlow: SequenceFlow): string => {
  const config = SEQUENCE_FLOW_CONFIG[sequenceFlow];
  return config ? `${config.displayName}: ${config.description}` : "";
};

// Timeline data transformation helpers

export enum ActivityType {
  VOTE = "VOTE",
  INTERACTION = "INTERACTION",
  TASK = "TASK",
}

export interface Activity {
  id: string;
  type: ActivityType;
  date: string;
  description: string;
  details?: string;
  milestone?: MilestoneKey;
  created_by?: string;
}

export interface VoteActivity extends Activity {
  type: ActivityType.VOTE;
  voting_mapping: {
    compass_cid: string;
    voting_record_id: string;
    engagement_record_id: string;
    confidence_score: number;
    rationale: string;
    is_cross_manager: boolean;
    matching_factors: string;
    interaction_class: InteractionClass;
    interaction_class_rationale: string;
    linkage_type: LinkageType;
    linkage_type_rationale: string;
    sequence_flow: SequenceFlow;
    sequence_flow_rationale: string;
    run_id: string;
    run_timestamp: string;
  };
}

export interface InteractionActivity extends Activity {
  type: ActivityType.INTERACTION;
  interactionTitle?: string;
  momentum?: Momentum;
  escalations?: string[];
  sdgs?: string[];
}

export interface TaskActivity extends Activity {
  type: ActivityType.TASK;
  assignee?: string;
  deadline?: string;
  priority?: Priority;
  notes?: TimelineActivityNote[];
  status?: TaskStatus;
  is_flag?: boolean;
}

export type TimelineActivity =
  | VoteActivity
  | InteractionActivity
  | TaskActivity;

const parseMilestone = (milestone?: string): MilestoneKey | undefined => {
  if (!milestone) return undefined;
  const key = milestone.toUpperCase() as keyof typeof MilestoneKey;
  return MilestoneKey[key] || undefined;
};

const parseMomentum = (momentum?: string): Momentum | undefined => {
  if (!momentum) return undefined;
  const key = momentum.toUpperCase() as keyof typeof Momentum;
  return Momentum[key] || undefined;
};

const parsePriority = (priority?: string): Priority | undefined => {
  if (!priority) return undefined;
  const key = priority.toUpperCase() as keyof typeof Priority;
  return Priority[key] || undefined;
};

const parseTaskStatus = (status?: string): TaskStatus | undefined => {
  const normalized = status?.toUpperCase().replace(/\s+/g, "_");
  return Object.values(TaskStatus).find((s) => s === normalized);
};

export const transformTimelineResponse = (
  data: TimelineActivityResponse[],
): TimelineActivity[] => {
  // First, transform all activities
  const activities = data.map((item) => {
    const base: Activity = {
      id: item.id,
      type: item.type as ActivityType,
      date: item.date,
      description: item.description,
      details: item.details,
      milestone: parseMilestone(item.extras?.milestone),
    };

    switch (item.type) {
      case ActivityType.VOTE:
        return {
          ...base,
          type: ActivityType.VOTE,
          voting_mapping: {
            compass_cid: item.extras?.voting_mapping?.compass_cid || "",
            voting_record_id:
              item.extras?.voting_mapping?.voting_record_id || "",
            engagement_record_id:
              item.extras?.voting_mapping?.engagement_record_id || "",
            confidence_score:
              item.extras?.voting_mapping?.confidence_score || 0,
            rationale: item.extras?.voting_mapping?.rationale || "",
            is_cross_manager:
              item.extras?.voting_mapping?.is_cross_manager || false,
            matching_factors:
              item.extras?.voting_mapping?.matching_factors || "",
            interaction_class: (item.extras?.voting_mapping
              ?.interaction_class ||
              InteractionClass.CONSISTENT_REINFORCEMENT) as InteractionClass,
            interaction_class_rationale:
              item.extras?.voting_mapping?.interaction_class_rationale || "",
            linkage_type: (item.extras?.voting_mapping?.linkage_type ||
              LinkageType.THEMATIC_ALIGNMENT) as LinkageType,
            linkage_type_rationale:
              item.extras?.voting_mapping?.linkage_type_rationale || "",
            sequence_flow: (item.extras?.voting_mapping?.sequence_flow ||
              SequenceFlow.ENGAGEMENT_PRECEDES_VOTE) as SequenceFlow,
            sequence_flow_rationale:
              item.extras?.voting_mapping?.sequence_flow_rationale || "",
            run_id: item.extras?.voting_mapping?.run_id || "",
            run_timestamp: item.extras?.voting_mapping?.run_timestamp || "",
          },
        } as VoteActivity;

      case ActivityType.INTERACTION:
        return {
          ...base,
          type: ActivityType.INTERACTION,
          interactionTitle: item.extras?.interaction_title || undefined,
          momentum: parseMomentum(item.extras?.momentum),
          escalations: item.extras?.escalations || [],
          sdgs: item.extras?.sdgs || [],
        } as InteractionActivity;

      case ActivityType.TASK:
        return {
          ...base,
          type: ActivityType.TASK,
          assignee: item.extras?.assignee,
          deadline: item.extras?.deadline,
          priority: parsePriority(item.extras?.priority),
          notes: item.extras?.notes || [],
          status: parseTaskStatus(item.extras?.status),
          is_flag: item.extras?.is_flag,
        } as TaskActivity;

      default:
        return base as TimelineActivity;
    }
  });

  // Sort by date (oldest first) and inherit milestones from previous activities
  const sortedByDate = [...activities].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

  let lastMilestone: MilestoneKey = MilestoneKey.M1; // Default to M1 if none specified
  for (const activity of sortedByDate) {
    if (activity.milestone) {
      lastMilestone = activity.milestone;
    } else {
      activity.milestone = lastMilestone;
    }
  }

  return activities;
};
